import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { selectedVariant, productData } from "../store/product-store";
import { scrollToVariantPicker } from "../utils/knocking";
import {
  submitBackInStockRequest,
  themeSettings,
} from "../store/extended-offer-store";

// Global focus trap instance (loaded from CDN)
declare const focusTrap: any;

const FADE_DURATION_MS = 300;

/**
 * Extended Offer Modal - Back in Stock Alert Subscription
 *
 * This is a global modal instance that shows a form for users to sign up
 * for back-in-stock alerts when a product variant is sold out.
 *
 * Usage:
 * 1. Place <extended-offer-modal></extended-offer-modal> once in your layout
 * 2. Call window.extendedOfferModal?.open(variantId) to open the modal
 *
 * Sheet chrome: `src/tailwind.css` (`@layer components` → `.offer-modal-*`).
 * CBS sold-out: `snippets/cbs-notify-extended-offer.liquid` dispatches `open-extended-offer` (same modal).
 */
@customElement("extended-offer-modal")
export class ExtendedOfferModal extends LitElement {
  @property({ type: String, attribute: "data-cbs-title" }) cbsModalTitle =
    "Get on the list!";
  @property({ type: String, attribute: "data-cbs-subtitle" }) cbsModalSubtitle =
    "Sign up to be the first to know when the next Box launches!";

  @state() private _isOpen = false;
  @state() private _isAnimatedIn = false;
  @state() private _isClosing = false;
  @state() private _isLoading = false;
  @state() private _isSuccess = false;
  @state() private _errorMessage = "";
  @state() private _email = "";
  @state() private _subscribeToNewsletter = false;
  @state() private _currentVariant: any = null;
  @state() private _currentProduct: any = null;

  /** True when variant/product were supplied explicitly (collection cards). */
  private _hasExplicitData = false;
  private _previouslyFocused: HTMLElement | null = null;
  private _focusTrap: any = null;
  private _closeTimeoutId: number | null = null;

  constructor() {
    super();

    // Subscribe to variant and product changes from product-store (for product page).
    // Always read both signals so the effect stays subscribed; only apply the values
    // when the data wasn't set explicitly by a collection card.
    effect(() => {
      const variant = selectedVariant.value;
      const product = productData.value;
      if (!this._hasExplicitData) {
        this._currentVariant = variant;
        this._currentProduct = product;
      }
    });

    // Make this component globally accessible
    (window as any).extendedOfferModal = this;
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize focus trap
    this._focusTrap =
      typeof focusTrap !== "undefined"
        ? focusTrap.createFocusTrap(this, {
            initialFocus: () =>
              this.querySelector("#extended-offer-email") ||
              this.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              ) ||
              this,
            escapeDeactivates: true,
            allowOutsideClick: true,
            onDeactivate: () => {
              if (
                this._previouslyFocused &&
                typeof this._previouslyFocused.focus === "function"
              ) {
                this._previouslyFocused.focus();
              }
            },
          })
        : null;

    // Keyboard event listeners
    this.addEventListener("keydown", this._handleKeydown);

    // Listen for open-extended-offer events from collection cards
    document.addEventListener("open-extended-offer", this._handleOpenEvent as EventListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this._handleKeydown);
    document.removeEventListener("open-extended-offer", this._handleOpenEvent as EventListener);
    if (this._closeTimeoutId) {
      clearTimeout(this._closeTimeoutId);
    }
    document.body.style.overflow = "";
    (window as any).extendedOfferModal = null;
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated?.(changedProperties);
    if (this._isOpen && !this._isClosing && !this._isAnimatedIn) {
      requestAnimationFrame(() => {
        this._isAnimatedIn = true;
      });
    }
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  // Public API - called from add-to-cart button (product page)
  public open() {
    this._isOpen = true;
    this._isAnimatedIn = false;
    this._isClosing = false;
    this._resetForm();
    document.body.style.overflow = "hidden";
    this._previouslyFocused = document.activeElement as HTMLElement;
    if (this._focusTrap) this._focusTrap.activate();
  }

  // Public API - called from collection cards with explicit variant/product data
  public openWithData(variant: any, product: any) {
    this._hasExplicitData = true;
    this._currentVariant = variant;
    this._currentProduct = product;
    this.open();
  }

  // Handle custom event from collection cards
  private _handleOpenEvent = (e: CustomEvent) => {
    const { variant, product } = e.detail || {};
    if (variant && product) {
      this.openWithData(variant, product);
    } else {
      this.open();
    }
  };

  private _closeWithAnimation() {
    if (this._isClosing) return;
    this._isClosing = true;

    this._closeTimeoutId = window.setTimeout(() => {
      this._closeTimeoutId = null;
      this._isClosing = false;
      this._isAnimatedIn = false;
      this._close();
    }, FADE_DURATION_MS);
  }

  private _close() {
    this._isOpen = false;
    document.body.style.overflow = "";
    if (this._focusTrap) this._focusTrap.deactivate();
    // Drop explicit collection-card data so the product-store (product page) takes
    // over again. On the product page there is no explicit data, so the store-synced
    // values are kept intact for the next open.
    if (this._hasExplicitData) {
      this._hasExplicitData = false;
      this._currentVariant = selectedVariant.value;
      this._currentProduct = productData.value;
    }
  }

  private _resetForm() {
    this._email = "";
    this._subscribeToNewsletter = false;
    this._isLoading = false;
    this._isSuccess = false;
    this._errorMessage = "";
  }

  private _handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this._isOpen) {
      e.stopPropagation();
      this._closeWithAnimation();
    }
  };

  private _handleBackdropClick = (e: Event) => {
    if (e.target === e.currentTarget) {
      this._closeWithAnimation();
    }
  };

  private async _handleSubmit(e: Event) {
    e.preventDefault();

    if (!this._currentVariant || this._isLoading) return;

    // Validate email
    const emailInput = this.querySelector(
      "#extended-offer-email"
    ) as HTMLInputElement;
    if (!emailInput?.checkValidity()) {
      this._errorMessage = "Please enter a valid email address.";
      emailInput?.setAttribute("aria-invalid", "true");
      emailInput?.setAttribute("aria-describedby", "extended-offer-error");
      return;
    }

    this._isLoading = true;
    this._errorMessage = "";

    const response = await submitBackInStockRequest({
      email: this._email,
      variantId: this._currentVariant.id,
      subscribeToNewsletter: this._subscribeToNewsletter,
    });

    this._isLoading = false;

    if (response.success) {
      this._isSuccess = true;
      emailInput?.setAttribute("aria-invalid", "false");
      emailInput?.setAttribute("aria-describedby", "extended-offer-success");
    } else {
      this._errorMessage =
        response.message || "An error occurred. Please try again.";
      emailInput?.setAttribute("aria-invalid", "true");
      emailInput?.setAttribute("aria-describedby", "extended-offer-error");
    }
  }

  private _handleEmailInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this._email = input.value;
  }

  private _handleNewsletterChange(e: Event) {
    const checkbox = e.target as HTMLInputElement;
    this._subscribeToNewsletter = checkbox.checked;
  }

  /**
   * Close the modal, then (product page only) scroll the variant picker to the
   * bottom of the viewport. On the collection page the modal just closes and the
   * card's own variant selector is revealed — no scrolling.
   */
  private _handleChangeVariant(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    scrollToVariantPicker();
    this._closeWithAnimation();
  }

  /** CBS Boxes use locale title/subtitle instead of the product title. */
  private _isCbsBoxProduct(product: { tags?: unknown } | null): boolean {
    const tags = product?.tags;
    if (Array.isArray(tags)) return tags.includes("cbs-box");
    if (typeof tags === "string") {
      return tags
        .split(",")
        .map((tag) => tag.trim())
        .includes("cbs-box");
    }
    return false;
  }

  /**
   * Vendor stays. CBS Boxes show a bold locale title with a regular-weight
   * subtitle underneath. Other products keep the bold product title.
   */
  private _renderTitleBlock(
    productVendor: string,
    productTitle: string,
    isCbsBox: boolean,
    sizeClass: string,
    productTitleClass: string
  ) {
    return html`
      <div class="text-caption">${productVendor}</div>
      ${isCbsBox
        ? html`
            <div class="${sizeClass} font-bold">${this.cbsModalTitle}</div>
            <div class="${sizeClass} font-normal">${this.cbsModalSubtitle}</div>
          `
        : html`<div class="${productTitleClass}">${productTitle}</div>`}
    `;
  }

  render() {
    if (!this._isOpen) return html``;

    const settings = themeSettings.value;
    const showNewsletter = settings?.showNewsletterCheckbox ?? false;
    const isVisible = this._isAnimatedIn && !this._isClosing;

    // Build variant display info
    const variantOptions: { name: string; value: string }[] = [];
    if (this._currentProduct && this._currentVariant) {
      const optionNames = this._currentProduct.options || [];
      for (let i = 0; i < optionNames.length; i++) {
        const optionValue = this._currentVariant[`option${i + 1}`];
        if (optionValue && optionValue !== "Default Title") {
          variantOptions.push({
            name: optionNames[i],
            value: optionValue,
          });
        }
      }
    }

    // Product image URL (variant or product featured image)
    const imageSrc =
      this._currentVariant?.featured_image?.src ||
      (typeof this._currentProduct?.featured_image === "string"
        ? this._currentProduct.featured_image
        : this._currentProduct?.featured_image?.src) ||
      null;

    const productTitle =
      this._currentProduct?.title || this._currentVariant?.title || "";
    const productVendor = this._currentProduct?.vendor ?? "";
    const isCbsBox = this._isCbsBoxProduct(this._currentProduct);
    const dialogHeading = isCbsBox
      ? this.cbsModalTitle
      : "Notify Me When Available";
    const hasMultipleVariants =
      (this._currentProduct?.variants?.length ?? 0) > 1 ||
      this._currentProduct?.has_only_default_variant === false;

    return html`
      <!-- Backdrop with blur - sticky bottom bar (shared .offer-modal-* chrome) -->
      <div
        class="offer-modal-backdrop ${isVisible ? "opacity-100" : "opacity-0"}"
        role="dialog"
        aria-modal="true"
        aria-labelledby="extended-offer-modal-heading"
        aria-hidden="${!isVisible}"
        @click=${this._handleBackdropClick}
      >
        <h2 id="extended-offer-modal-heading" class="sr-only">
          ${dialogHeading}
        </h2>
        <div
          class="offer-modal-panel ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"}"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="offer-modal-layout-mobile">
            <div class="offer-modal-row-mobile-top">
              ${imageSrc
                ? html`
                    <div class="offer-modal-media-wrap">
                      <img
                        src="${imageSrc}"
                        alt="${productTitle}"
                        class="offer-modal-thumb-img-sm"
                      />
                      <span class="offer-modal-badge-sold-out">
                        Sold Out
                      </span>
                    </div>
                  `
                : html``}
              <div class="offer-modal-stack-product">
                <div class="offer-modal-stack-titles">
                  ${this._renderTitleBlock(
                    productVendor,
                    productTitle,
                    isCbsBox,
                    "text-base",
                    "text-base font-bold truncate"
                  )}
                </div>
                ${variantOptions.length > 0
                  ? html`
                      <div class="offer-modal-variant-row">
                        <div class="offer-modal-variant-pill">
                          ${variantOptions.map(
                            (opt) => html`
                              <span class="offer-modal-variant-label">
                                ${opt.value}
                              </span>
                            `
                          )}
                        </div>
                        ${hasMultipleVariants
                          ? html`
                              <button
                                type="button"
                                class="offer-modal-btn-change-variant"
                                @click=${this._handleChangeVariant}
                              >
                                Change
                              </button>
                            `
                          : html``}
                      </div>
                    `
                  : ""}
              </div>
            </div>
            <div class="offer-modal-column-form-mobile">
              ${this._errorMessage
                ? html`
                    <div
                      id="extended-offer-error"
                      role="alert"
                      aria-live="assertive"
                      class="offer-modal-alert"
                    >
                      ${this._errorMessage}
                    </div>
                  `
                : ""}
              ${this._isSuccess
                ? html`
                    <div
                      id="extended-offer-success"
                      role="status"
                      aria-live="polite"
                      class="offer-modal-success-msg"
                    >
                      Success! We'll notify you when this item is back in stock.
                    </div>
                  `
                : html`
                    <form
                      id="extended-offer-form"
                      class="offer-modal-form-row-mobile"
                      @submit=${this._handleSubmit}
                    >
                      <input
                        type="email"
                        id="extended-offer-email"
                        name="email"
                        placeholder="Enter your email"
                        required
                        .value=${this._email}
                        @input=${this._handleEmailInput}
                        ?disabled=${this._isLoading}
                        aria-invalid="${this._errorMessage ? "true" : "false"}"
                        aria-describedby="${this._errorMessage
                          ? "extended-offer-error"
                          : ""}"
                        class="offer-modal-input-email-mobile"
                      />
                      <button
                        type="submit"
                        class="btn offer-modal-submit-mobile"
                        ?disabled=${this._isLoading}
                      >
                        ${this._isLoading ? "Processing..." : "Get Notified"}
                      </button>
                    </form>
                  `}
              ${showNewsletter && !this._isSuccess && !this._isLoading
                ? html`
                    <label class="offer-modal-newsletter-label">
                      <input
                        type="checkbox"
                        id="subscribe-to-mail-list"
                        .checked=${this._subscribeToNewsletter}
                        @change=${this._handleNewsletterChange}
                        class="offer-modal-newsletter-checkbox"
                      />
                      <span class="offer-modal-newsletter-span"
                        >Subscribe to our mailing list</span
                      >
                    </label>
                  `
                : ""}
            </div>
            <button
              type="button"
              class="btn w-full rounded-md bg-[var(--color-danger-200)] text-[var(--color-danger-800)] hover:bg-[var(--color-danger-300)] md:min-h-[60px]"
              aria-label="Close modal"
              @click=${() => this._closeWithAnimation()}
            >
              Close
            </button>
            <p class="offer-modal-privacy-mobile">
              We respect your privacy and will not share your personal
              information.
            </p>
          </div>

          <div class="offer-modal-layout-desktop">
            ${imageSrc
              ? html`
                  <div class="offer-modal-img-desktop-rel">
                    <img
                      src="${imageSrc}"
                      alt="${productTitle}"
                      class="offer-modal-thumb-img-lg"
                    />
                    <span class="offer-modal-badge-sold-out">
                      Sold Out
                    </span>
                  </div>
                `
              : ""}
            <div class="offer-modal-desktop-flex-outer">
              <div class="offer-modal-desktop-flex-inner">
                <div class="offer-modal-desktop-title-row">
                  <div class="hidden min-w-0 lg:flex lg:flex-col">
                    ${this._renderTitleBlock(
                      productVendor,
                      productTitle,
                      isCbsBox,
                      "text-lg",
                      "text-lg font-bold break-words"
                    )}
                  </div>
                  ${variantOptions.length > 0
                    ? html`
                        <div class="offer-modal-desktop-variant-wrap">
                          <div class="offer-modal-variant-pill-lg">
                            ${variantOptions.map(
                              (opt) => html`
                                <span class="offer-modal-variant-label">
                                  ${opt.value}
                                </span>
                              `
                            )}
                          </div>
                        </div>
                      `
                    : ""}
                </div>
                <div class="offer-modal-desktop-form-column">
                  ${this._errorMessage
                    ? html`
                        <div
                          id="extended-offer-error"
                          role="alert"
                          aria-live="assertive"
                          class="offer-modal-alert"
                        >
                          ${this._errorMessage}
                        </div>
                      `
                    : ""}
                  ${this._isSuccess
                    ? html`
                        <div
                          id="extended-offer-success"
                          role="status"
                          aria-live="polite"
                          class="offer-modal-success-msg"
                        >
                          Success! We'll notify you when this item is back in stock.
                        </div>
                      `
                    : html`
                        <form
                          id="extended-offer-form"
                          class="offer-modal-form-row-desktop"
                          @submit=${this._handleSubmit}
                        >
                          <input
                            type="email"
                            id="extended-offer-email"
                            name="email"
                            placeholder="Enter your email"
                            required
                            .value=${this._email}
                            @input=${this._handleEmailInput}
                            ?disabled=${this._isLoading}
                            aria-invalid="${this._errorMessage ? "true" : "false"}"
                            aria-describedby="${this._errorMessage
                              ? "extended-offer-error"
                              : ""}"
                            class="offer-modal-input-email-desktop"
                          />
                          <button
                            type="submit"
                            class="btn offer-modal-submit-desktop"
                            ?disabled=${this._isLoading}
                          >
                            ${this._isLoading ? "Processing..." : "Get Notified"}
                          </button>
                        </form>
                      `}
                  ${showNewsletter && !this._isSuccess && !this._isLoading
                    ? html`
                        <label class="offer-modal-newsletter-label">
                          <input
                            type="checkbox"
                            id="subscribe-to-mail-list"
                            .checked=${this._subscribeToNewsletter}
                            @change=${this._handleNewsletterChange}
                            class="offer-modal-newsletter-checkbox"
                          />
                          <span class="offer-modal-newsletter-span"
                            >Subscribe to our mailing list</span
                          >
                        </label>
                      `
                    : ""}
                  <p class="offer-modal-privacy-desktop">
                    We respect your privacy and will not share your personal
                    information.
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="btn min-h-[60px] min-w-[150px] shrink-0 rounded-md bg-[var(--color-danger-200)] text-[var(--color-danger-800)] hover:bg-[var(--color-danger-300)] md:min-h-[60px]"
                aria-label="Close modal"
                @click=${() => this._closeWithAnimation()}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

// TypeScript declaration for global access
declare global {
  interface Window {
    extendedOfferModal?: ExtendedOfferModal;
    Shopify?: {
      routes?: {
        root?: string;
      };
    };
  }
}
